import { StrictMode, createContext, useContext, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './stile.css';
import { api, type Organizzazione, type Utente } from './api';
import { Caricamento, FornitoreAvvisi, useCarico } from './componenti/base';
import { Accesso } from './pagine/accesso';
import { Cruscotto } from './pagine/cruscotto';
import { Previsionale } from './pagine/previsionale';
import { Documenti } from './pagine/documenti';
import { DettaglioDocumento } from './pagine/dettaglio-documento';
import { LetteraCompensazione } from './pagine/lettera-compensazione';
import { Importazioni } from './pagine/importazioni';
import { Ritenute } from './pagine/ritenute';
import { F24Pagina } from './pagine/f24';
import { Regole } from './pagine/regole';
import { Tesoreria } from './pagine/tesoreria';
import { Riconciliazione } from './pagine/riconciliazione';
import { Scadenzario } from './pagine/scadenzario';
import { Anagrafiche } from './pagine/anagrafiche';
import { Budget } from './pagine/budget';
import { Categorie } from './pagine/categorie';
import { Impostazioni } from './pagine/impostazioni';
import { Utenti } from './pagine/utenti';
import { Registro } from './pagine/registro';
import { Integrazioni } from './pagine/integrazioni';

interface Sessione {
  utente: Utente;
  organizzazione: Organizzazione;
  ricarica: () => void;
}
const ContestoSessione = createContext<Sessione | null>(null);
export const useSessione = () => {
  const s = useContext(ContestoSessione);
  if (!s) throw new Error('Sessione non disponibile');
  return s;
};

interface Voce {
  a: string;
  testo: string;
  esatto?: boolean;
}
interface Gruppo {
  titolo: string;
  voci: Voce[];
}

// Il menu è raggruppato per area, non una lista piatta: dieci voci uguali in
// fila costringono a rileggerle una per una, sezioni con un titolo si scorrono
// a colpo d'occhio. Impostazioni e Utenti non stanno qui: sono voci di
// servizio, e vivono nel menu sotto il proprio nome in alto a destra — dove
// si va quando si cerca «le mie cose», non «il mio lavoro».
const GRUPPI: Gruppo[] = [
  {
    titolo: 'Panoramica',
    voci: [
      { a: '/', testo: 'Cruscotto', esatto: true },
      { a: '/previsionale', testo: 'Previsionale' },
    ],
  },
  {
    titolo: 'Documenti',
    voci: [
      { a: '/fatture/attive', testo: 'Fatture attive' },
      { a: '/fatture/passive', testo: 'Fatture passive' },
      { a: '/scadenzario', testo: 'Scadenzario' },
      { a: '/importazioni', testo: 'Importazioni' },
      { a: '/ritenute', testo: 'Ritenute d’acconto' },
      { a: '/f24', testo: 'F24' },
    ],
  },
  {
    titolo: 'Banca',
    voci: [
      { a: '/tesoreria', testo: 'Conti e movimenti' },
      { a: '/riconciliazione', testo: 'Riconciliazione' },
      { a: '/regole', testo: 'Regole automatiche' },
    ],
  },
  {
    titolo: 'Pianificazione',
    voci: [{ a: '/budget', testo: 'Budget' }],
  },
  {
    titolo: 'Anagrafica',
    voci: [
      { a: '/anagrafiche', testo: 'Anagrafiche' },
      { a: '/categorie', testo: 'Categorie' },
    ],
  },
];

// Lista piatta per la barra mobile, dove i titoli di sezione non stanno: là le
// voci scorrono in orizzontale. Le voci di servizio restano fuori anche qui:
// sul telefono il menu del proprio nome c'è comunque.
const VOCI_PIATTE: Voce[] = GRUPPI.flatMap((g) => g.voci);

// Iniziali per l'avatar del pannello utente (stile AdminLTE): prime lettere di
// nome e cognome, o le prime due dell'email se il nome manca.
function iniziali(s: string): string {
  const parti = s.trim().split(/[\s@.]+/).filter(Boolean);
  const lettere = (parti.length >= 2 ? [parti[0], parti[1]] : [parti[0] ?? '']).map((p) => p.charAt(0));
  return lettere.join('').toUpperCase() || '?';
}

function Guscio() {
  const { utente, organizzazione } = useSessione();
  const posizione = useLocation();

  // La skin scelta dall'utente vive come attributo sull'<html>: il CSS ridefinisce
  // i token `--color-skin*` in base a `data-skin`. Senza attributo vale il blu.
  useEffect(() => {
    document.documentElement.setAttribute('data-skin', utente.tema);
  }, [utente.tema]);

  return (
    <div className="flex min-h-screen flex-col bg-contenuto">
      {/* Barra superiore in stile AdminLTE: riquadro del logo (scuro) accanto
          alla barra skin. Il blu qui è cornice, non un dato. */}
      <header className="sticky top-0 z-40 flex h-[50px] shrink-0 text-white shadow-sm print:hidden">
        {/* Il riquadro del logo. Se l'azienda ne ha caricato uno prende il posto
            della scritta: serve a riconoscere a colpo d'occhio di chi sono i
            numeri, che con più istanze aperte è una domanda vera. L'indirizzo
            porta in coda la data di aggiornamento, altrimenti dopo una
            sostituzione il browser continuerebbe a mostrare quello in cache. */}
        {/* Centrato, come il riquadro del logo di AdminLTE: un logo allineato a
            sinistra dentro un riquadro largo quanto la barra sembra spostato,
            e il margine che avanza a destra si legge come un errore. Poco
            imbottitura ai lati, così un logo largo ha spazio. */}
        <div className="hidden w-60 shrink-0 items-center justify-center bg-skin-scuro px-3 text-lg font-semibold tracking-tight md:flex">
          {organizzazione.logoPresente ? (
            <img
              src={`/api/organizzazione/logo?v=${encodeURIComponent(organizzazione.logoAggiornatoIl ?? '')}`}
              alt={organizzazione.ragioneSociale}
              className="max-h-[28px] max-w-full object-contain"
            />
          ) : (
            'Mastro'
          )}
        </div>
        <div className="flex flex-1 items-center justify-between gap-3 bg-skin px-4">
          <span className="font-semibold md:hidden">Mastro</span>
          <span className="truncate text-sm text-white/90" title={organizzazione.ragioneSociale}>
            {organizzazione.ragioneSociale}
          </span>
          <MenuUtente />
        </div>
      </header>

      {/* Su schermo stretto le voci scorrono in orizzontale, sotto l'header. */}
      <nav className="sticky top-[50px] z-30 flex gap-1 overflow-x-auto border-b border-black/20 bg-barra px-2 py-2 md:hidden print:hidden">
        {VOCI_PIATTE.map((v) => (
          <NavLink
            key={v.a}
            to={v.a}
            end={v.esatto}
            className={({ isActive }) =>
              `whitespace-nowrap rounded px-3 py-1.5 text-xs ${
                isActive ? 'bg-barra-attiva text-white' : 'text-barra-testo'
              }`
            }
          >
            {v.testo}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-1">
        {/* Sidebar scura in stile AdminLTE. */}
        <nav className="hidden w-60 shrink-0 flex-col bg-barra text-barra-testo md:flex print:hidden">
          {/* Pannello utente: avatar con iniziali, nome e ruolo. */}
          <div className="flex items-center gap-3 border-b border-black/20 px-5 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-skin text-sm font-semibold text-white">
              {iniziali(utente.nome ?? utente.email)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm text-white">{utente.nome ?? utente.email}</p>
              <p className="text-[11px] text-barra-tenue">
                {utente.ruolo === 'ADMIN' ? 'Amministratore' : 'Operatore'}
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {GRUPPI.map((gruppo) => (
              <div key={gruppo.titolo}>
                <p className="bg-barra-intestazione px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-barra-tenue">
                  {gruppo.titolo}
                </p>
                <ul>
                  {gruppo.voci.map((v) => (
                    <li key={v.a}>
                      <NavLink
                        to={v.a}
                        end={v.esatto}
                        className={({ isActive }) =>
                          `block border-l-[3px] py-2.5 pr-4 pl-[13px] text-sm transition-colors ${
                            isActive
                              ? 'border-skin bg-barra-attiva text-white'
                              : 'border-transparent hover:border-skin hover:bg-barra-attiva hover:text-white'
                          }`
                        }
                      >
                        {v.testo}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </nav>

        {/* `pb-[38px]`: l'altezza esatta del piè di pagina fisso. Senza, l'ultima
            riga di ogni tabella resterebbe nascosta sotto. */}
        <main key={posizione.pathname} className="min-w-0 flex-1 pb-[38px] print:pb-0">
        <Routes>
          <Route path="/" element={<Cruscotto />} />
          <Route path="/previsionale" element={<Previsionale />} />
          <Route path="/fatture/attive" element={<Documenti tipo="ATTIVA" />} />
          <Route path="/fatture/passive" element={<Documenti tipo="PASSIVA" />} />
          <Route path="/fatture/:id" element={<DettaglioDocumento />} />
          <Route path="/importazioni" element={<Importazioni />} />
          <Route path="/ritenute" element={<Ritenute />} />
          <Route path="/f24" element={<F24Pagina />} />
          <Route path="/compensazioni/:id/lettera" element={<LetteraCompensazione />} />
          <Route path="/tesoreria" element={<Tesoreria />} />
          <Route path="/riconciliazione" element={<Riconciliazione />} />
          <Route path="/regole" element={<Regole />} />
          <Route path="/scadenzario" element={<Scadenzario />} />
          <Route path="/budget" element={<Budget />} />
          <Route path="/anagrafiche" element={<Anagrafiche />} />
          <Route path="/categorie" element={<Categorie />} />
          <Route path="/impostazioni" element={<Impostazioni />} />
          <Route path="/utenti" element={<Utenti />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/impostazioni/integrazioni" element={<Integrazioni />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>
      </div>

      <Piede />
    </div>
  );
}

/**
 * Piè di pagina in stile AdminLTE 2: fondo chiaro, filo sopra, copyright a
 * sinistra e versione a destra.
 *
 * È **fisso** come l'header, e non in coda al contenuto: la versione serve nel
 * momento in cui si segnala un problema, e in una pagina lunga come il
 * previsionale — dodici mesi di righe — quella in coda non la vede nessuno.
 * Il contenuto ha sotto lo spazio corrispondente, così l'ultima riga non
 * finisce mai nascosta dietro.
 *
 * A sinistra si allinea alla barra laterale, dove c'è: sotto la barra non ci
 * passa, come nell'originale.
 */
function Piede() {
  // Solo l'anno in corso, calcolato: non conosciamo l'anno di partenza del
  // progetto e scriverne uno a mano sarebbe un'ipotesi, non un dato.
  const anno = new Date().getFullYear();
  return (
    <footer className="fixed inset-x-0 bottom-0 z-30 flex h-[38px] items-center justify-between border-t border-inchiostro-200 bg-white px-4 text-xs text-inchiostro-500 md:left-60 print:hidden">
      <span>
        <strong className="font-semibold text-inchiostro-600">Copyright © {anno} Erretielle TLC</strong>. Tutti i
        diritti riservati.
      </span>
      {/* Su schermo stretto resta solo il copyright, come fa AdminLTE: la
          versione è un dato di servizio e non vale la riga che occuperebbe. */}
      <span className="hidden sm:inline">
        <strong className="font-semibold text-inchiostro-600">Mastro</strong> {__VERSIONE_MASTRO__}
      </span>
    </footer>
  );
}

function Applicazione() {
  const { dati, caricamento, ricarica } = useCarico(
    () => api.get<{ utente: Utente; organizzazione: Organizzazione }>('/api/io').catch(() => null),
    [],
  );

  if (caricamento) return <Caricamento testo="Apertura di Mastro…" />;
  if (!dati) return <Accesso onEntrato={ricarica} />;
  // Chi entra con la password ricevuta dall'amministratore non va oltre finché
  // non se ne sceglie una sua: fin lì, le sue credenziali le conosce un altro.
  if (dati.utente.deveCambiarePassword) return <CambioObbligatorio onFatto={ricarica} />;

  return (
    <ContestoSessione.Provider value={{ ...dati, ricarica }}>
      <Guscio />
    </ContestoSessione.Provider>
  );
}

/**
 * Il menu sotto il proprio nome, in alto a destra.
 *
 * Ci stanno le voci che riguardano **te** e non il lavoro: le impostazioni
 * dell'azienda, le persone che possono entrare, e l'uscita. In fondo alla barra
 * laterale ci finivano per esclusione, ed era il posto dove si guarda per
 * ultimo — mentre il proprio nome in alto a destra è il primo posto in cui
 * chiunque cerca «le mie cose».
 */
function MenuUtente() {
  const { utente } = useSessione();
  const [aperto, setAperto] = useState(false);
  const posizione = useLocation();

  // Cambiando pagina il menu si chiude da solo: restare aperto sopra una
  // schermata nuova fa sembrare che il clic non abbia funzionato.
  useEffect(() => setAperto(false), [posizione.pathname]);

  useEffect(() => {
    if (!aperto) return;
    const chiudi = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      // Un clic *dentro* il menu non lo chiude qui: lo chiude il cambio di
      // pagina. Chiuderlo subito toglierebbe la voce da sotto il puntatore
      // prima che riceva il clic.
      if (e.target instanceof Element && e.target.closest('[data-menu-utente]')) return;
      setAperto(false);
    };
    document.addEventListener('click', chiudi);
    document.addEventListener('keydown', chiudi);
    return () => {
      document.removeEventListener('click', chiudi);
      document.removeEventListener('keydown', chiudi);
    };
  }, [aperto]);

  const voce =
    'block w-full px-4 py-2 text-left text-sm text-inchiostro-700 transition-colors hover:bg-inchiostro-100 focus-visible:bg-inchiostro-100 focus-visible:outline-none';

  return (
    <div className="relative shrink-0" data-menu-utente>
      <button
        className="flex items-center gap-2 rounded px-2 py-1 text-sm text-white/90 transition-colors hover:bg-black/10 hover:text-white"
        aria-haspopup="menu"
        aria-expanded={aperto}
        onClick={() => setAperto((v) => !v)}
      >
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-black/20 text-[11px] font-semibold">
          {iniziali(utente.nome ?? utente.email)}
        </span>
        <span className="hidden max-w-44 truncate sm:inline">{utente.nome ?? utente.email}</span>
        <span aria-hidden="true" className="text-[10px] opacity-80">
          &#9662;
        </span>
      </button>

      {aperto && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-60 overflow-hidden rounded-md border border-inchiostro-200 bg-white py-1 shadow-lg"
        >
          <div className="border-b border-inchiostro-200 px-4 pb-2 pt-1">
            <p className="truncate text-sm font-medium text-inchiostro-900">{utente.nome ?? utente.email}</p>
            <p className="truncate text-xs text-inchiostro-500">
              {utente.nome ? utente.email : utente.ruolo === 'ADMIN' ? 'Amministratore' : 'Operatore'}
            </p>
          </div>

          <NavLink to="/impostazioni" role="menuitem" className={voce}>
            Impostazioni
          </NavLink>
          {utente.ruolo === 'ADMIN' && (
            <NavLink to="/utenti" role="menuitem" className={voce}>
              Utenti
            </NavLink>
          )}
          {/* Il registro non è dell'azienda: è di chi assiste. La voce compare
              solo a chi sta in MASTRO_SUPERADMIN — e il controllo che conta sta
              comunque sulle rotte, che a chiunque altro rispondono 404. */}
          {utente.superadmin && (
            <NavLink to="/registro" role="menuitem" className={voce}>
              Registro di sistema
            </NavLink>
          )}

          <div className="mt-1 border-t border-inchiostro-200 pt-1">
            <button
              role="menuitem"
              className={voce}
              onClick={async () => {
                await api.post('/api/uscita');
                location.href = '/';
              }}
            >
              Esci
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Schermata di cambio password al primo accesso. Non è un avviso ignorabile:
 * è l'unica cosa che si può fare, perché una password che conosce anche chi
 * l'ha creata non è ancora una password.
 */
function CambioObbligatorio({ onFatto }: { onFatto: () => void }) {
  const [attuale, setAttuale] = useState('');
  const [nuova, setNuova] = useState('');
  const [ripeti, setRipeti] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [attesa, setAttesa] = useState(false);

  const invia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nuova !== ripeti) {
      setErrore('Le due password non coincidono.');
      return;
    }
    setAttesa(true);
    setErrore(null);
    try {
      await api.post('/api/io/password', { attuale, nuova });
      onFatto();
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Cambio non riuscito.');
      setAttesa(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-inchiostro-100 p-4">
      <form onSubmit={invia} className="w-full max-w-sm rounded-lg border border-inchiostro-200 bg-white p-6 shadow-lg">
        <h1 className="text-lg font-semibold tracking-tight">Scegli la tua password</h1>
        <p className="mt-1 mb-4 text-sm text-inchiostro-500">
          Quella con cui sei entrato te l’ha data l’amministratore, e la conosce anche lui. Da qui in poi la sai solo tu.
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Password ricevuta</span>
          <input
            type="password"
            className="campo w-full"
            value={attuale}
            onChange={(ev) => setAttuale(ev.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Nuova password</span>
          <input
            type="password"
            className="campo w-full"
            value={nuova}
            onChange={(ev) => setNuova(ev.target.value)}
            minLength={10}
            required
          />
          <span className="mt-1 block text-xs text-inchiostro-500">Almeno 10 caratteri.</span>
        </label>
        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium">Ripetila</span>
          <input
            type="password"
            className="campo w-full"
            value={ripeti}
            onChange={(ev) => setRipeti(ev.target.value)}
            required
          />
        </label>

        {errore && (
          <p role="alert" className="mb-3 rounded-md border border-allerta/30 bg-allerta-chiaro p-2.5 text-sm text-allerta">
            {errore}
          </p>
        )}

        <button className="bottone-primario w-full" disabled={attesa || nuova.length < 10}>
          {attesa ? 'Salvataggio…' : 'Salva ed entra'}
        </button>
      </form>
    </div>
  );
}

createRoot(document.getElementById('radice')!).render(
  <StrictMode>
    <BrowserRouter>
      <FornitoreAvvisi>
        <Applicazione />
      </FornitoreAvvisi>
    </BrowserRouter>
  </StrictMode>,
);
